import { useState, useEffect, useCallback, useRef } from "react";
import { firebaseService } from "../services";
import { isSafari } from "@firebase/util";
import { useAuthStore } from "../stores/zustand";

/**
 * Custom hook for managing Firebase push notifications
 *
 * Provides centralized Firebase notification functionality including:
 * - Permission management
 * - Token registration
 * - Message listening
 * - Safari push alert support
 * - Notification banner management
 *
 * @returns {Object} Firebase notification state and operations
 */
export const useFirebaseNotifications = () => {
  // State for notification management
  const [isTokenFound, setIsTokenFound] = useState(false);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);
  const [safariPushAlertToken, setSafariPushAlertToken] = useState(null);
  const [currentNotification, setCurrentNotification] = useState({
    title: "",
    body: "",
    icon: "",
    url: "",
  });
  const [showToast, setShowToast] = useState(false);
  const initializedRef = useRef(false);
  const lastNotificationIdRef = useRef(null);
  const email = useAuthStore((state) => state.user.email);

  const isElectron = useRef(
    typeof window !== "undefined" && !!window.electronPushReceiver,
  ).current;
  /**
   * Checks if push notifications are supported in the current environment
   * @returns {boolean} True if push notifications are supported
   */
  const isPushNotificationSupported = useCallback(() => {
    if (isElectron) {
      // In Electron, push notifications are supported through the electron-push-receiver
      return !!window.electronPushReceiver;
    }
    // For web environments, use the Firebase service check
    return firebaseService.isPushNotificationSupported();
  }, [isElectron]);

  /**
   * Checks if the current browser is iOS Safari
   * @returns {boolean} True if running on iOS Safari
   */
  const isIOSSafari = useCallback(() => {
    const ua = navigator.userAgent;
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPad on iOS 13+
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|OPiOS/.test(ua);

    return isIOS && isSafari;
  }, []);

  // Initializes notification banner visibility based on permission status

  const initializeNotificationBanner = useCallback(() => {
    if (isElectron) {
      setShowNotificationBanner(false);
      setIsTokenFound(true);
      return;
    }

    // Hide banner for iOS Safari
    if (isIOSSafari()) {
      setShowNotificationBanner(false);
      return;
    }

    const permission =
      "Notification" in window ? Notification.permission : "default";

    if (permission === "granted") {
      setShowNotificationBanner(false);
      setIsPermissionDenied(false);
    } else if (permission === "denied") {
      setShowNotificationBanner(true);
      setIsPermissionDenied(true);
    } else {
      // "default" - never asked
      setShowNotificationBanner(true);
      setIsPermissionDenied(false);
    }
  }, [isElectron, isIOSSafari]);

  /**
   * Handles incoming foreground messages
   * @param {Object} payload - Message payload from Firebase
   */
  const handleMessageReceived = useCallback(async (payload) => {
    const notifId =
      payload.data?.tag ||
      payload.data?.id ||
      `${payload.data?.title}:${payload.data?.body}`;

    if (lastNotificationIdRef.current === notifId) {
      return;
    }
    lastNotificationIdRef.current = notifId;
    setTimeout(() => {
      lastNotificationIdRef.current = null;
    }, 30000);

    const notificationData = {
      title:
        payload.data?.title ||
        payload.notification?.title ||
        "New Notification",
      body:
        payload.data?.body ||
        payload.notification?.body ||
        "Test notification received",
      icon: payload.data?.icon || payload.notification?.icon || "",
      url: payload.data?.url || "",
    };
    setCurrentNotification(notificationData);
    setShowToast(true);

    await firebaseService.handleForegroundMessage(payload, (data) => {});
  }, []);

  // Sets up Firebase message listener

  const setupMessageListener = useCallback(() => {
    if (isElectron) {
      return () => {}; // Return an empty cleanup function
    }

    let unsubscribeFn = null;

    firebaseService.onMessageListener(handleMessageReceived).then((unsub) => {
      unsubscribeFn = unsub;
    });

    return () => {
      if (unsubscribeFn && typeof unsubscribeFn === "function") {
        unsubscribeFn();
      }
    };
  }, [isElectron, handleMessageReceived]);

  /**
   * Requests notification permission and registers token
   * @param {Event} event - The click event (optional)
   * @returns {Promise<boolean>} Success status
   */
  const handleEnablePushNotifications = useCallback(
    async (event) => {
      if (event) {
        event.preventDefault();
      }

      if (isElectron) {
        setShowNotificationBanner(false);
        setIsTokenFound(true);
        return true;
      }

      if (isSafari()) {
        try {
          const webPushId = `${process.env.REACT_APP_SAFARI_WEB_PUSH_ID}`;
          const serviceUrl = `${process.env.REACT_APP_FIREBASE_API_HOST}`;
          if (!window.safari?.pushNotification) {
            console.error("Safari Push Notifications not available");
            return false;
          }

          const permissionData =
            window.safari.pushNotification.permission(webPushId);
          return new Promise((resolve) => {
            let callbackFired = false;
            const checkRemotePermission = function (permissionResult) {
              if (callbackFired) {
              }
              callbackFired = true;

              if (permissionResult.permission === "default") {
                // Only request if permission is 'default' (not asked yet)
                try {
                  window.safari.pushNotification.requestPermission(
                    serviceUrl,
                    webPushId,
                    { email: email }, // Optional user info
                    function (result) {
                      checkRemotePermission(result);
                    }, // Callback function (recursive)
                  );
                } catch (err) {
                  resolve(false);
                }
              } else if (permissionResult.permission === "denied") {
                // User previously denied
                alert(
                  "Please enable notifications in Safari Settings > Websites > Notifications",
                );
                resolve(false);
              } else if (permissionResult.permission === "granted") {
                if (permissionResult.deviceToken) {
                  try {
                    setShowNotificationBanner(false);
                    setIsTokenFound(true);
                    registerSafariToken(permissionResult.deviceToken);
                    resolve(true);
                  } catch (error) {
                    console.error("Some error occured:", error);
                    resolve(false);
                  }
                } else {
                  resolve(false);
                }
              }
            };
            checkRemotePermission(permissionData);
          });
        } catch (error) {
          console.error("Error enabling Safari push notifications:", error);
          return false;
        }
      }

      // Non-Safari browsers use Firebase
      try {
        const success =
          await firebaseService.requestPermissionAndGetToken(setIsTokenFound);
        if (success) {
          setShowNotificationBanner(false);
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error("Error enabling push notifications:", error);
        return false;
      }
    },
    [isElectron],
  );

  /**
   * Registers Safari push alert token
   * @param {string} subscriberId - Safari push alert subscriber ID
   * @returns {Promise<boolean>} Success status
   */
  const registerSafariToken = useCallback(async (subscriberId) => {
    try {
      await firebaseService.registerSafariToken(subscriberId);
      setSafariPushAlertToken(subscriberId);
      return true;
    } catch (error) {
      console.error("Failed to register Safari token:", error);
      return false;
    }
  }, []);

  /**
   * Opens browser-specific notification guide
   * @param {Event} event - The click event
   */
  const handleOpenNotificationGuide = useCallback((event) => {
    if (event) {
      event.preventDefault();
    }

    const userAgent = navigator.userAgent;
    let notificationGuideURL;

    if (userAgent.includes("Chrome")) {
      notificationGuideURL = "https://support.google.com/chrome/answer/3220216";
    } else if (userAgent.includes("Firefox")) {
      notificationGuideURL =
        "https://support.mozilla.org/en-US/kb/push-notifications-firefox";
    } else if (userAgent.includes("Edge")) {
      notificationGuideURL =
        "https://support.microsoft.com/microsoft-edge/notifications-in-microsoft-edge-eb6cc739-2b49-0a1a-6c7e-7e2f65715357";
    } else if (userAgent.includes("Trident") || userAgent.includes("MSIE")) {
      notificationGuideURL =
        "https://support.microsoft.com/windows/change-notification-settings-in-internet-explorer-cb4cc3df-8c4a-b458-f5b2-9b7904b53f8f";
    } else if (userAgent.includes("Safari")) {
      notificationGuideURL =
        "https://support.apple.com/en-ie/guide/safari/sfri40734/mac";
    } else {
      notificationGuideURL = "https://support.google.com/chrome/answer/3220216";
    }

    window.open(notificationGuideURL, "_blank", "noreferrer");
  }, []);

  // Dismisses the current toast notification

  const dismissToast = useCallback(() => {
    setShowToast(false);
  }, []);

  const handleNotificationClick = useCallback(() => {
    if (currentNotification.url) {
      window.open(currentNotification.url, "_blank");
    }
  }, [currentNotification.url]);

  /**
   * Gets current notification permission status
   * @returns {string} Permission status
   */
  const getNotificationPermission = useCallback(() => {
    if (isElectron) {
      return "granted"; // Assume granted in Electron
    }
    return firebaseService.getNotificationPermission();
  }, [isElectron]);

  /**
   * Refreshes FCM token (useful for token rotation)
   * @returns {Promise<boolean>} Success status
   */
  const refreshFirebaseToken = useCallback(async () => {
    if (isElectron) {
      return true;
    }

    try {
      const token =
        await firebaseService.fetchAndRegisterToken(setIsTokenFound);
      return !!token;
    } catch (error) {
      console.error("Failed to refresh Firebase token:", error);
      return false;
    }
  }, [isElectron]);

  // Safari Push Alert initialization
  useEffect(() => {
    if (isSafari() && !isElectron) {
      const webPushId = "web.com.deskdynamics"; // Website Push ID
      const permissionData =
        window.safari?.pushNotification?.permission(webPushId);
      if (
        permissionData?.permission === "granted" &&
        permissionData.deviceToken
      ) {
        // User already granted permission previously
        firebaseService
          .registerSafariToken(permissionData.deviceToken)
          .then(() => {
            setSafariPushAlertToken(permissionData.deviceToken);
          })
          .catch((error) => {
            console.error("Failed to register existing Safari token:", error);
          });
      } else if (permissionData?.permission === "default") {
        // Show the banner so user can click to enable
        setShowNotificationBanner(true);
      }
    }
  }, [isElectron]);

  // Listen for custom Electron notification events
  useEffect(() => {
    if (!isElectron) return;

    const handleElectronNotification = (event) => {
      handleMessageReceived(event.detail);
    };

    window.addEventListener(
      "electron-notification",
      handleElectronNotification,
    );

    return () => {
      window.removeEventListener(
        "electron-notification",
        handleElectronNotification,
      );
    };
  }, [isElectron, handleMessageReceived]);

  // Initialize notification banner and message listener
  useEffect(() => {
    let cleanupListener = null;

    const timer = setTimeout(async () => {
      if (!initializedRef.current) {
        initializeNotificationBanner();
        cleanupListener = setupMessageListener();
        initializedRef.current = true;

        if (
          !isElectron &&
          !isSafari() &&
          Notification.permission === "granted"
        ) {
          await firebaseService.fetchAndRegisterToken(setIsTokenFound);
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cleanupListener) {
        cleanupListener();
      }
    };
  }, [email]);

  // Reset initialization when user changes (logout/login)
useEffect(() => {
    initializedRef.current = false;
}, [email]);

  // Reset dedup guard when user changes
  useEffect(() => {
    lastNotificationIdRef.current = null;
  }, [email]);

  // Return hook interface
  return {
    // State
    isTokenFound,
    showNotificationBanner,
    safariPushAlertToken,
    currentNotification,
    showToast,
    isElectron,

    // Operations
    handleEnablePushNotifications,
    registerSafariToken,
    handleOpenNotificationGuide,
    dismissToast,
    refreshFirebaseToken,

    // Utilities
    getNotificationPermission,
    isPushNotificationSupported,
    setShowNotificationBanner,
    isPermissionDenied,

    // Event handlers
    onNotificationClick: handleNotificationClick,
  };
};
